import {ActionContext, Module} from 'vuex'
import {RootState} from '@/store'
import {ResponseLogin} from "@/services/auth/entities/ResponseLogin";
import AuthService from "@/services/auth/AuthService";

export interface AuthUser {
    username: string
    role: string
    office: string
    name: string
    renovationDate?: string
    isBirthday: boolean
}

interface Credentials {
    username: string,
    password: string
}

export interface AuthState {
    token: string | null,
    user: AuthUser | null,
    isLoadingAuth: boolean,
    hasShownRenovationMessage: boolean,
    hasShownBirthdayMessage: boolean,
}

const auth: Module<AuthState, RootState> = {
    namespaced: true,
    state: {
        token: localStorage.getItem('token') || null,
        user: null,
        isLoadingAuth: false,
        hasShownRenovationMessage: false,
        hasShownBirthdayMessage: false,
    },
    mutations: {
        SET_TOKEN(state: AuthState, token: string): void {
            state.token = token
            localStorage.setItem('token', token);
        },
        SET_USER(state: AuthState, user: AuthUser): void {
            state.user = user;
        },
        SET_LOADING_AUTH(state: AuthState, isLoading: boolean): void {
            state.isLoadingAuth = isLoading;
        },
        SET_HAS_SHOWN_RENOVATION_MESSAGE(state: AuthState, hasShown: boolean): void {
            state.hasShownRenovationMessage = hasShown;
        },
        SET_HAS_SHOWN_BIRTHDAY_MESSAGE(state: AuthState, hasShown: boolean): void {
            state.hasShownBirthdayMessage = hasShown;
        },
        CLEAR_AUTH(state: AuthState): void {
            state.token = null;
            state.user = null;
            localStorage.removeItem('token');
        }
    },
    getters: {
        isAuthenticated(state: AuthState): boolean {
            return !!state.token;
        },
        isLoadingAuth(state: AuthState): boolean {
            return state.isLoadingAuth;
        },
        getUser(state: AuthState): AuthUser | null {
            return state.user;
        },
        hasShownRenovationMessage(state: AuthState): boolean {
            return state.hasShownRenovationMessage;
        },
        hasShownBirthdayMessage(state: AuthState): boolean {
            return state.hasShownBirthdayMessage;
        },
    },
    actions: {
        async login({commit}: ActionContext<AuthState, RootState>, credentials: Credentials): Promise<void> {
            commit('SET_LOADING_AUTH', true);
            try {
                const response: ResponseLogin = await AuthService.login(credentials);
                commit('SET_TOKEN', response.token);
                commit('SET_USER', response.user);
            } finally {
                commit('SET_LOADING_AUTH', false);
            }
        },
        async logout({commit}: ActionContext<AuthState, RootState>): Promise<void> {
            commit('CLEAR_AUTH');
        },
        async restoreSession({commit}: ActionContext<AuthState, RootState>): Promise<void> {
            commit('SET_LOADING_AUTH', true);
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    commit('CLEAR_AUTH');
                    return;
                }
                const user: AuthUser = await AuthService.getSession();
                commit('SET_TOKEN', token);
                commit('SET_USER', user);
            } finally {
                commit('SET_LOADING_AUTH', false);
            }
        }
    }
}

export default auth
